import React, { useMemo } from 'react';
import { Column, ColumnWithLooseAccessor } from 'react-table';
import { isFunction, isString } from 'lodash';
import cc from 'classcat';

interface OutputFunctionArguments {
  row: {
    original: any;
  };
  cell: {
    value: any;
  };
}

type OutputFunction = (args: OutputFunctionArguments) => React.ReactNode;

export interface VerticalTableProps<T extends object> {
  columns: Array<Column<T>>;
  data: Array<T>;
  dependencies?: any[];
  getRowStyle?: (data?: T) => React.CSSProperties;
  getRowClass?: (data?: T) => string;
  style?: any;
  striped?: boolean;
  bareCell?: boolean;
}

const VerticalTable = <T extends object>({
  columns: _columns,
  data: _data,
  dependencies,
  style,
  striped,
  bareCell
}: VerticalTableProps<T>): JSX.Element => {
  dependencies = dependencies || [];
  const columns = useMemo(() => _columns, [_columns, ...dependencies]);
  const data = useMemo(() => _data, [_data]);
  const tableClasses = cc(['vtable', striped ? 'vtable--striped' : null]);
  return (
    <div className={tableClasses} style={style}>
      {columns.map((column, i) => (
        <div key={i} className="vtable-tr">
          <div className="vtable-th">
            {isFunction(column.Header) ? (
              column.Header(null)
            ) : isString(column.Header) ? (
              <b>{column.Header}</b>
            ) : (
              column.Header
            )}
          </div>
          {data
            ? data.map((entry, j) => {
                const isLast = j === data.length - 1;
                const cellClasses = cc([
                  'vtable-td',
                  isLast ? 'vtable-td--last' : null
                ]);
                let key: string;
                let output: React.ReactElement<any>;
                if (column.accessor) {
                  key = column.accessor as string;
                  output = entry[key];
                }
                if ((column as ColumnWithLooseAccessor).Cell) {
                  output = (
                    (column as ColumnWithLooseAccessor).Cell as OutputFunction
                  )({
                    row: {
                      original: entry
                    },
                    cell: {
                      value: key ? entry[key] : undefined
                    }
                  }) as React.ReactElement<any>;
                }
                if (bareCell) {
                  return React.cloneElement(output, {
                    key: j,
                    className:
                      (output.props.className || '') + ' ' + cellClasses
                  });
                }
                return (
                  <div key={j} className={cellClasses}>
                    {output}
                  </div>
                );
              })
            : null}
        </div>
      ))}
    </div>
  );
};

export default VerticalTable;
